import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  BuildingOffice2Icon,
  CheckCircleIcon,
  ArrowRightCircleIcon,
  ClockIcon,
  EyeIcon,
  TrashIcon,
  UserPlusIcon,
  EnvelopeIcon,
  XCircleIcon,
  IdentificationIcon,
  XMarkIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import api from '@/lib/api';
import type { Membership } from '@/types';
import { useMyOrganizations, useTeamActivity, type MyOrganizationMembership } from '@/hooks/useApi';
import { useAuthStore } from '@/stores/authStore';
import ActivityModal from '@/components/ActivityModal';

const ROLE_BADGE: Record<string, string> = {
  super_admin: 'bg-purple-100 text-purple-700',
  admin: 'bg-blue-100 text-blue-700',
  accountant: 'bg-emerald-100 text-emerald-700',
  user: 'bg-gray-100 text-gray-700',
};

function formatRelative(iso: string | null, t: (k: string) => string): string {
  if (!iso) return t('team.never');
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return t('team.justNow');
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d`;
  return new Date(iso).toLocaleDateString();
}

function formatExpiresIn(iso: string, t: (k: string, opts?: Record<string, unknown>) => string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return t('team.linkExpired');
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return t('team.expiresInMinutes', { count: mins });
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return t('team.expiresInHours', { count: hrs });
  const days = Math.floor(hrs / 24);
  return t('team.expiresInDays', { count: days });
}

interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  createdAt: string;
  expiresAt: string;
  expired: boolean;
  invitedBy: { email: string; firstName: string | null; lastName: string | null } | null;
}

export default function TeamPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: memberships, isLoading } = useMyOrganizations();
  const { data: teamActivity } = useTeamActivity();
  const currentOrgId = useAuthStore((s) => s.organizationId);
  const currentUser = useAuthStore((s) => s.user);
  const setOrganizationId = useAuthStore((s) => s.setOrganizationId);
  const [activityUser, setActivityUser] = useState<{ id: string; name: string } | null>(null);
  const [contactOrg, setContactOrg] = useState<MyOrganizationMembership | null>(null);

  const handleSwitch = async (orgId: string, orgName: string) => {
    setOrganizationId(orgId);
    await qc.invalidateQueries();
    toast.success(t('team.switched', { org: orgName }));
    navigate('/');
  };

  const isAdminOfCurrent = memberships?.some(
    (m) =>
      m.organization.id === currentOrgId &&
      (m.role === 'super_admin' || m.role === 'admin'),
  );

  // ── Members management (admins only) ──
  const [members, setMembers] = useState<Membership[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('user');
  const [inviting, setInviting] = useState(false);

  const reloadMembers = useCallback(async () => {
    try {
      const res = await api.get('/organizations/current/members');
      setMembers(res.data.data);
    } catch {
      /* ignore */
    }
  }, []);

  const reloadInvitations = useCallback(async () => {
    try {
      const res = await api.get('/organizations/current/invitations');
      setPendingInvitations(res.data.data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (isAdminOfCurrent) {
      reloadMembers();
      reloadInvitations();
    } else {
      setMembers([]);
      setPendingInvitations([]);
    }
  }, [isAdminOfCurrent, currentOrgId, reloadMembers, reloadInvitations]);

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setInviting(true);
    try {
      const res = await api.post('/organizations/current/members', {
        email: inviteEmail,
        role: inviteRole,
      });
      const invited = !!res.data?.data?.invited;
      toast.success(
        invited ? t('settings.invitationSent') : t('settings.memberInvited'),
      );
      setInviteEmail('');
      await Promise.all([reloadMembers(), reloadInvitations()]);
    } catch {
      toast.error(t('common.error'));
    }
    setInviting(false);
  }

  async function handleRemoveMember(id: string) {
    if (!confirm(t('common.confirmDelete'))) return;
    try {
      await api.delete(`/organizations/current/members/${id}`);
      setMembers((prev) => prev.filter((m) => m.id !== id));
      toast.success(t('common.deleted'));
    } catch {
      toast.error(t('common.error'));
    }
  }

  async function handleToggleMemberStatus(id: string, nextIsActive: boolean) {
    const confirmMsg = nextIsActive ? t('team.confirmUnblock') : t('team.confirmBlock');
    if (!confirm(confirmMsg)) return;
    try {
      await api.patch(`/organizations/current/members/${id}`, { isActive: nextIsActive });
      setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, isActive: nextIsActive } : m)));
      toast.success(nextIsActive ? t('team.memberUnblocked') : t('team.memberBlocked'));
    } catch (error) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(message || t('common.error'));
    }
  }

  async function handleRevokeInvitation(id: string) {
    if (!confirm(t('team.confirmRevokeInvitation'))) return;
    try {
      await api.delete(`/organizations/current/invitations/${id}`);
      setPendingInvitations((prev) => prev.filter((i) => i.id !== id));
      toast.success(t('team.invitationRevoked'));
    } catch {
      toast.error(t('common.error'));
    }
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('team.title')}</h1>
        <p className="text-sm text-gray-500 mt-1">{t('team.subtitle')}</p>
      </div>

      {/* ── Organizations switcher ── */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
          {t('team.yourOrganizations')}
        </h2>

        {isLoading && <div className="text-sm text-gray-500">{t('common.loading')}</div>}

        {!isLoading && memberships && memberships.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-500">
            {t('team.noOrganizations')}
          </div>
        )}

        <div className="space-y-3">
          {memberships?.map((m) => {
            const isCurrent = m.organization.id === currentOrgId;
            return (
              <div
                key={m.organization.id}
                className={`flex items-center justify-between gap-4 p-4 rounded-xl border transition-colors ${
                  isCurrent
                    ? 'border-primary-300 bg-primary-50/50'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <div
                    className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      isCurrent
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    <BuildingOffice2Icon className="w-6 h-6" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate">
                      {m.organization.name}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span
                        className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                          ROLE_BADGE[m.role] ?? ROLE_BADGE.user
                        }`}
                      >
                        {t(`settings.roles.${m.role}`)}
                      </span>
                      {m.organization.vatNumber && (
                        <span className="text-xs text-gray-400">
                          {m.organization.vatNumber}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => setContactOrg(m)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
                    title={t('team.contactInfo')}
                  >
                    <IdentificationIcon className="w-4 h-4" />
                    {t('team.contact')}
                  </button>
                  {isCurrent ? (
                    <span className="flex items-center gap-1.5 text-sm font-medium text-primary-700">
                      <CheckCircleIcon className="w-5 h-5" />
                      {t('team.current')}
                    </span>
                  ) : (
                    <button
                      onClick={() => handleSwitch(m.organization.id, m.organization.name)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
                    >
                      {t('team.switch')}
                      <ArrowRightCircleIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Members management (admins only) ── */}
      {isAdminOfCurrent && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            {t('team.manageMembers')}
          </h2>

          <form
            onSubmit={handleInvite}
            className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col sm:flex-row gap-3"
          >
            <input
              type="email"
              className="input flex-1"
              placeholder={t('settings.inviteEmail')}
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
            />
            <select
              className="input w-full sm:w-auto"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value)}
            >
              <option value="user">{t('settings.roles.user')}</option>
              <option value="accountant">{t('settings.roles.accountant')}</option>
              <option value="admin">{t('settings.roles.admin')}</option>
            </select>
            <button
              type="submit"
              className="btn btn-primary inline-flex items-center justify-center gap-1.5"
              disabled={inviting}
            >
              <UserPlusIcon className="w-4 h-4" />
              {inviting ? t('common.saving') : t('settings.invite')}
            </button>
          </form>

          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">{t('settings.member')}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t('settings.role')}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t('team.status')}</th>
                  <th className="px-4 py-2.5 w-32" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {members.map((m) => {
                  const isSuper = m.role === 'super_admin';
                  const isSelf = m.user?.id === currentUser?.id;
                  const isBlocked = m.isActive === false;
                  return (
                    <tr key={m.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">
                          {m.user?.firstName} {m.user?.lastName}
                        </div>
                        <div className="text-xs text-gray-400">{m.user?.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                            ROLE_BADGE[m.role] ?? ROLE_BADGE.user
                          }`}
                        >
                          {t(`settings.roles.${m.role}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                            isBlocked ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                          }`}
                        >
                          {isBlocked ? t('team.blocked') : t('team.active')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          {!isSuper && !isSelf && (
                            <button
                              onClick={() => handleToggleMemberStatus(m.id, isBlocked)}
                              className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded transition-colors ${
                                isBlocked
                                  ? 'text-emerald-700 hover:bg-emerald-50'
                                  : 'text-amber-700 hover:bg-amber-50'
                              }`}
                              title={isBlocked ? t('team.unblock') : t('team.block')}
                            >
                              {isBlocked ? <CheckCircleIcon className="w-4 h-4" /> : <NoSymbolIcon className="w-4 h-4" />}
                            </button>
                          )}
                          {!isSuper && (
                            <button
                              onClick={() => handleRemoveMember(m.id)}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors"
                              title={t('common.delete')}
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {members.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-400">
                      {t('common.noResults')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── Pending invitations (admins only) ── */}
      {isAdminOfCurrent && pendingInvitations.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            {t('team.pendingInvitations')}
          </h2>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">{t('settings.inviteEmail')}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t('settings.role')}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t('team.status')}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t('team.linkValidity')}</th>
                  <th className="px-4 py-2.5 w-20" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pendingInvitations.map((inv) => (
                  <tr key={inv.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <EnvelopeIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="font-medium text-gray-900">{inv.email}</span>
                      </div>
                      {inv.invitedBy && (
                        <div className="text-xs text-gray-400 mt-0.5 ml-6">
                          {t('team.invitedBy', {
                            name:
                              `${inv.invitedBy.firstName ?? ''} ${inv.invitedBy.lastName ?? ''}`.trim() ||
                              inv.invitedBy.email,
                          })}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                          ROLE_BADGE[inv.role] ?? ROLE_BADGE.user
                        }`}
                      >
                        {t(`settings.roles.${inv.role}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${
                          inv.expired
                            ? 'bg-red-100 text-red-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {inv.expired ? (
                          <XCircleIcon className="w-3 h-3" />
                        ) : (
                          <ClockIcon className="w-3 h-3" />
                        )}
                        {inv.expired ? t('team.linkExpired') : t('team.pending')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatExpiresIn(inv.expiresAt, t)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => handleRevokeInvitation(inv.id)}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors"
                        title={t('team.revoke')}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── Members activity (admins only) ── */}
      {isAdminOfCurrent && teamActivity && teamActivity.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
            {t('team.membersActivity')}
          </h2>
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">{t('team.member')}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t('settings.role')}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t('team.lastLogin')}</th>
                  <th className="text-left px-4 py-2.5 font-medium">{t('team.lastAction')}</th>
                  <th className="text-right px-4 py-2.5 font-medium">{t('team.actions30d')}</th>
                  <th className="text-right px-4 py-2.5 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {teamActivity.map((m) => {
                  const fullName =
                    `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() || m.email;
                  return (
                    <tr key={m.userId} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{fullName}</div>
                        <div className="text-xs text-gray-400">{m.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                            ROLE_BADGE[m.role] ?? ROLE_BADGE.user
                          }`}
                        >
                          {t(`settings.roles.${m.role}`)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <span className="inline-flex items-center gap-1">
                          <ClockIcon className="w-3.5 h-3.5 text-gray-400" />
                          {formatRelative(m.lastLoginAt, t)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600">
                        <div>{formatRelative(m.lastActivityAt, t)}</div>
                        {m.lastActivityAction && (
                          <div className="text-xs text-gray-400">{m.lastActivityAction}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-900">
                        {m.actionsLast30Days}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() =>
                            setActivityUser({ id: m.userId, name: fullName })
                          }
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 rounded transition-colors"
                          title={t('activity.viewActivity')}
                        >
                          <EyeIcon className="w-4 h-4" />
                          {t('activity.viewActivity')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {activityUser && (
        <ActivityModal
          mode="user"
          userId={activityUser.id}
          isOpen={true}
          onClose={() => setActivityUser(null)}
          title={`${t('activity.title')} — ${activityUser.name}`}
        />
      )}

      {contactOrg && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setContactOrg(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-gray-200">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 truncate">
                  {contactOrg.organization.name}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">{t('team.contactInfo')}</p>
              </div>
              <button
                onClick={() => setContactOrg(null)}
                className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                aria-label={t('common.close')}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="px-5 py-4 overflow-y-auto space-y-5">
              {/* Organization details */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  {t('team.organizationDetails')}
                </h4>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {contactOrg.organization.email && (
                    <div>
                      <dt className="text-xs text-gray-500">{t('team.fields.email')}</dt>
                      <dd className="text-gray-800 break-all">
                        <a href={`mailto:${contactOrg.organization.email}`} className="hover:underline">
                          {contactOrg.organization.email}
                        </a>
                      </dd>
                    </div>
                  )}
                  {contactOrg.organization.phone && (
                    <div>
                      <dt className="text-xs text-gray-500">{t('team.fields.phone')}</dt>
                      <dd className="text-gray-800">
                        <a href={`tel:${contactOrg.organization.phone}`} className="hover:underline">
                          {contactOrg.organization.phone}
                        </a>
                      </dd>
                    </div>
                  )}
                  {contactOrg.organization.website && (
                    <div>
                      <dt className="text-xs text-gray-500">{t('team.fields.website')}</dt>
                      <dd className="text-gray-800 break-all">
                        <a
                          href={contactOrg.organization.website.startsWith('http') ? contactOrg.organization.website : `https://${contactOrg.organization.website}`}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:underline"
                        >
                          {contactOrg.organization.website}
                        </a>
                      </dd>
                    </div>
                  )}
                  {contactOrg.organization.vatNumber && (
                    <div>
                      <dt className="text-xs text-gray-500">{t('team.fields.vatNumber')}</dt>
                      <dd className="text-gray-800">{contactOrg.organization.vatNumber}</dd>
                    </div>
                  )}
                  {contactOrg.organization.enterpriseNumber && (
                    <div>
                      <dt className="text-xs text-gray-500">{t('team.fields.enterpriseNumber')}</dt>
                      <dd className="text-gray-800">{contactOrg.organization.enterpriseNumber}</dd>
                    </div>
                  )}
                  {(contactOrg.organization.address || contactOrg.organization.city || contactOrg.organization.postalCode || contactOrg.organization.country) && (
                    <div className="sm:col-span-2">
                      <dt className="text-xs text-gray-500">{t('team.fields.address')}</dt>
                      <dd className="text-gray-800 whitespace-pre-line">
                        {[
                          contactOrg.organization.address,
                          [contactOrg.organization.postalCode, contactOrg.organization.city].filter(Boolean).join(' '),
                          contactOrg.organization.country,
                        ]
                          .filter(Boolean)
                          .join('\n')}
                      </dd>
                    </div>
                  )}
                  {contactOrg.organization.iban && (
                    <div>
                      <dt className="text-xs text-gray-500">{t('team.fields.iban')}</dt>
                      <dd className="text-gray-800 font-mono text-xs">{contactOrg.organization.iban}</dd>
                    </div>
                  )}
                  {contactOrg.organization.bic && (
                    <div>
                      <dt className="text-xs text-gray-500">{t('team.fields.bic')}</dt>
                      <dd className="text-gray-800 font-mono text-xs">{contactOrg.organization.bic}</dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Admin contacts */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  {t('team.administrators')}
                </h4>
                {(!contactOrg.contacts || contactOrg.contacts.length === 0) ? (
                  <p className="text-sm text-gray-500">{t('team.noContacts')}</p>
                ) : (
                  <div className="space-y-3">
                    {contactOrg.contacts.map((c) => {
                      const fullName = `${c.firstName} ${c.lastName}`.trim() || c.email;
                      return (
                        <div key={c.id} className="border border-gray-200 rounded-lg p-3">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="font-medium text-gray-900 text-sm truncate">{fullName}</div>
                            <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${ROLE_BADGE[c.role] ?? ROLE_BADGE.user}`}>
                              {t(`settings.roles.${c.role}`)}
                            </span>
                          </div>
                          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                            <div className="sm:col-span-2">
                              <dt className="text-xs text-gray-500">{t('team.fields.email')}</dt>
                              <dd className="text-gray-800 break-all flex items-center gap-1.5">
                                <a href={`mailto:${c.email}`} className="hover:underline">{c.email}</a>
                                {c.emailVerified && (
                                  <CheckCircleIcon className="w-4 h-4 text-emerald-500" title={t('team.fields.verified') ?? ''} />
                                )}
                              </dd>
                            </div>
                            {c.phone && (
                              <div>
                                <dt className="text-xs text-gray-500">{t('team.fields.phone')}</dt>
                                <dd className="text-gray-800">
                                  <a href={`tel:${c.phone}`} className="hover:underline">{c.phone}</a>
                                </dd>
                              </div>
                            )}
                            {c.locale && (
                              <div>
                                <dt className="text-xs text-gray-500">{t('team.fields.locale')}</dt>
                                <dd className="text-gray-800 uppercase">{c.locale}</dd>
                              </div>
                            )}
                            {c.timezone && (
                              <div>
                                <dt className="text-xs text-gray-500">{t('team.fields.timezone')}</dt>
                                <dd className="text-gray-800">{c.timezone}</dd>
                              </div>
                            )}
                            {c.status && (
                              <div>
                                <dt className="text-xs text-gray-500">{t('team.fields.status')}</dt>
                                <dd className="text-gray-800">{c.status}</dd>
                              </div>
                            )}
                            {c.lastLoginAt && (
                              <div>
                                <dt className="text-xs text-gray-500">{t('team.fields.lastLogin')}</dt>
                                <dd className="text-gray-800">{new Date(c.lastLoginAt).toLocaleString()}</dd>
                              </div>
                            )}
                            {c.memberSince && (
                              <div>
                                <dt className="text-xs text-gray-500">{t('team.fields.memberSince')}</dt>
                                <dd className="text-gray-800">{new Date(c.memberSince).toLocaleDateString()}</dd>
                              </div>
                            )}
                          </dl>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
